import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { requestsApi, Request } from '../../services/api';
import UTCTime from '../../components/UTCTime/UTCTime';
import { getNationByCode, getNationByName } from '../../constants/nations';
import RequestQuickView from '../../components/RequestQuickView/RequestQuickView';
import { exportToExcel, formatDateForExcel } from '../../utils/excelExport';
import { MessageSquare, HandCoins, Users, Eye, Plus, ArrowUp, ArrowDown, ArrowUpDown, FileSearch, Inbox, FileSpreadsheet, Save, X, HelpCircle } from 'lucide-react';
import './RequestsList.css';

type SortField = 'date' | 'requestId' | 'from' | 'category' | 'status';
type ViewType = 'all' | 'active' | 'myActive' | 'myRequests' | 'myOffers' | 'inTransit' | 'saved';

interface SavedView {
  baseView: Exclude<ViewType, 'saved'>;
  statusFilter: string;
  categoryFilter: string;
  urgencyFilter: string;
  searchQuery: string;
  sortBy: SortField;
  sortOrder: 'asc' | 'desc';
  pageSize: number;
}

function RequestsList() {
  const { t } = useTranslation();
  const { user, isReadOnlyInstance } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  // Active view (UI display) and effective base view (data filtering)
  const [activeView, setActiveView] = useState<ViewType>('all');
  const [effectiveBaseView, setEffectiveBaseView] = useState<Exclude<ViewType, 'saved'>>('all');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Quick View Modal
  const [quickViewRequestId, setQuickViewRequestId] = useState<string | null>(null);
  const [showQuickView, setShowQuickView] = useState(false);

  // Transit Hub Popup
  const [hubPopupRequestId, setHubPopupRequestId] = useState<string | null>(null);
  const closeHubPopup = useCallback(() => setHubPopupRequestId(null), []);
  useEffect(() => {
    if (hubPopupRequestId) {
      const handler = () => closeHubPopup();
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [hubPopupRequestId, closeHubPopup]);

  // Check if a saved view exists
  const [hasSavedView, setHasSavedView] = useState(false);

  useEffect(() => {
    loadRequests();
    setHasSavedView(!!localStorage.getItem('savedRequestView'));

    // Read filters from URL query params (e.g., from Dashboard navigation)
    const status = searchParams.get('status');
    if (status) {
      setStatusFilter(status);
    }
    const category = searchParams.get('category');
    if (category) {
      setCategoryFilter(category);
    }
  }, [searchParams]);

  // Get view-filtered requests (before user filters)
  // Uses effectiveBaseView so "saved" views apply their base view's data filter
  const viewFilteredRequests = useMemo(() => {
    let filtered = [...requests];

    switch (effectiveBaseView) {
      case 'active':
        filtered = filtered.filter(r => ['PUBLISHED', 'UNDER_SELECTION'].includes(r.status));
        break;
      case 'myActive':
        filtered = filtered.filter(r =>
          ['PUBLISHED', 'UNDER_SELECTION'].includes(r.status) && r.creatorId === user?.id
        );
        break;
      case 'myRequests':
        filtered = filtered.filter(r => r.creatorId === user?.id);
        break;
      case 'myOffers':
        filtered = filtered.filter(r =>
          r.offers?.some(o => o.user?.id === user?.id)
        );
        break;
      case 'inTransit':
        filtered = filtered.filter(r =>
          r.status === 'IN_TRANSIT' && (
            r.creatorId === user?.id ||
            r.offers?.some(o => o.user?.id === user?.id)
          )
        );
        break;
      case 'all':
      default:
        break;
    }

    return filtered;
  }, [requests, effectiveBaseView, user?.id]);

  // Compute available filter values from the view-filtered data
  const availableStatuses = useMemo(() => {
    const statuses = new Set(viewFilteredRequests.map(r => r.status));
    return Array.from(statuses).sort();
  }, [viewFilteredRequests]);

  const availableCategories = useMemo(() => {
    const categories = new Set(viewFilteredRequests.map(r => r.category));
    return Array.from(categories).sort();
  }, [viewFilteredRequests]);

  const availableUrgencies = useMemo(() => {
    const urgencies = new Set(viewFilteredRequests.map(r => r.urgency));
    return Array.from(urgencies).sort();
  }, [viewFilteredRequests]);

  // Apply user filters + sorting
  const filteredRequests = useMemo(() => {
    let filtered = [...viewFilteredRequests];

    if (statusFilter) {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    if (categoryFilter) {
      filtered = filtered.filter(r => r.category === categoryFilter);
    }
    if (urgencyFilter) {
      filtered = filtered.filter(r => r.urgency === urgencyFilter);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(query) ||
        r.requestId.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        r.creator.nation.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'requestId':
          comparison = a.requestId.localeCompare(b.requestId);
          break;
        case 'from':
          comparison = a.creator.nation.localeCompare(b.creator.nation);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [viewFilteredRequests, statusFilter, categoryFilter, urgencyFilter, searchQuery, sortBy, sortOrder]);

  // Pagination computed values
  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / pageSize));
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, categoryFilter, urgencyFilter, searchQuery, activeView, pageSize]);

  // View counts for KPI cards
  const viewCounts = useMemo(() => {
    const active = requests.filter(r => ['PUBLISHED', 'UNDER_SELECTION'].includes(r.status));
    const myActive = active.filter(r => r.creatorId === user?.id);
    const myRequests = requests.filter(r => r.creatorId === user?.id);
    const myOffers = requests.filter(r => r.offers?.some(o => o.user?.id === user?.id));
    const inTransit = requests.filter(r =>
      r.status === 'IN_TRANSIT' && (
        r.creatorId === user?.id ||
        r.offers?.some(o => o.user?.id === user?.id)
      )
    );

    return {
      all: requests.length,
      active: active.length,
      myActive: myActive.length,
      myRequests: myRequests.length,
      myOffers: myOffers.length,
      inTransit: inTransit.length
    };
  }, [requests, user?.id]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await requestsApi.getAll();
      setRequests(response.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (view: ViewType) => {
    if (view === 'saved') {
      const saved = localStorage.getItem('savedRequestView');
      if (saved) {
        const sv: SavedView = JSON.parse(saved);
        setEffectiveBaseView(sv.baseView || 'all');
        setStatusFilter(sv.statusFilter);
        setCategoryFilter(sv.categoryFilter);
        setUrgencyFilter(sv.urgencyFilter);
        setSearchQuery(sv.searchQuery);
        setSortBy(sv.sortBy);
        setSortOrder(sv.sortOrder);
        setPageSize(sv.pageSize);
      }
    } else {
      setEffectiveBaseView(view);
      setStatusFilter('');
      setCategoryFilter('');
      setUrgencyFilter('');
      setSearchQuery('');
      setSortBy('date');
      setSortOrder('desc');
    }
    setActiveView(view);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setUrgencyFilter('');
    setSearchQuery('');
    setSortBy('date');
    setSortOrder('desc');
    setActiveView('all');
    setEffectiveBaseView('all');
    setCurrentPage(1);
  };

  const handleSaveView = () => {
    const view: SavedView = {
      baseView: effectiveBaseView,
      statusFilter,
      categoryFilter,
      urgencyFilter,
      searchQuery,
      sortBy,
      sortOrder,
      pageSize
    };
    localStorage.setItem('savedRequestView', JSON.stringify(view));
    setHasSavedView(true);
  };

  const handleColumnSort = (column: SortField) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIndicator = (column: SortField) => {
    if (sortBy !== column) return <span className="sort-indicator sort-inactive"><ArrowUpDown size={12} /></span>;
    return <span className="sort-indicator">{sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>;
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      DRAFT: 'badge-draft',
      UNDER_REVIEW: 'badge-review',
      PUBLISHED: 'badge-published',
      UNDER_SELECTION: 'badge-selection',
      ACCEPTED: 'badge-accepted',
      PLANNED: 'badge-planned',
      IN_TRANSIT: 'badge-transit',
      COMPLETED: 'badge-completed',
      CLOSED: 'badge-closed',
      WITHDRAWN: 'badge-withdrawn'
    };
    return statusMap[status] || 'badge-draft';
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      DRAFT: t('status.draft'),
      UNDER_REVIEW: t('status.underReview'),
      PUBLISHED: t('status.published'),
      UNDER_SELECTION: t('status.underSelection'),
      ACCEPTED: t('status.accepted'),
      PLANNED: t('status.planned'),
      IN_TRANSIT: t('status.inTransit'),
      COMPLETED: t('status.completed'),
      CLOSED: t('status.closed'),
      WITHDRAWN: t('status.withdrawn')
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      PERSONNEL: t('categories.personnel'),
      MATERIAL: t('categories.equipment'),
      MEDEVAC: t('categories.medevac'),
      TRANSPORT: 'Transport',
      OTHER: t('categories.other'),
      NEWS: t('categories.news')
    };
    return labels[category] || category;
  };

  const getCategoryBadgeClass = (category: string) => {
    const classes: { [key: string]: string } = {
      MEDEVAC: 'badge-category-medevac',
      PERSONNEL: 'badge-category-personnel',
      MATERIAL: 'badge-category-equipment',
      TRANSPORT: 'badge-category-transport',
      OTHER: 'badge-category-other',
      NEWS: 'badge-category-news'
    };
    return classes[category] || 'badge-category-other';
  };

  /** Compute patient priority summary for a request */
  const getTriageSummary = (patients?: any[]) => {
    if (!patients || patients.length === 0) return null;
    const p1 = patients.filter(p => p.priority === 'P1').length;
    const p2 = patients.filter(p => p.priority === 'P2').length;
    const p3 = patients.filter(p => p.priority === 'P3').length;
    if (p1 === 0 && p2 === 0 && p3 === 0) return null;
    return { p1, p2, p3 };
  };

  /** Format deadline countdown */
  const getDeadlineInfo = (answerBy?: string) => {
    if (!answerBy) return null;
    const deadline = new Date(answerBy);
    const now = new Date();
    const diffMs = deadline.getTime() - now.getTime();

    if (diffMs <= 0) return { text: 'Overdue', className: 'deadline-overdue' };

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 4) return { text: `${diffHours}h left`, className: 'deadline-critical' };
    if (diffHours < 24) return { text: `${diffHours}h left`, className: 'deadline-urgent' };
    if (diffDays < 3) return { text: `${diffDays}d left`, className: 'deadline-soon' };
    return { text: `${diffDays}d left`, className: 'deadline-ok' };
  };

  const resolveNation = (value: string) => getNationByCode(value) || getNationByName(value);

  const handleQuickView = (requestId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickViewRequestId(requestId);
    setShowQuickView(true);
  };

  const handleExportToExcel = () => {
    const exportData = filteredRequests.map(request => ({
      'Request ID': request.requestId,
      'Title': request.title,
      'Category': getCategoryLabel(request.category),
      'Urgency': request.urgency,
      'Status': getStatusLabel(request.status),
      'Creator Nation': request.creator.nation,
      'Exit Point': request.exitPoint?.name || '',
      'Patients': request.patients?.length || 0,
      'Offers': request.offers?.length || 0,
      'Created': formatDateForExcel(request.createdAt),
      'Description': request.description
    }));

    exportToExcel(exportData, `ARCOS_Requests_${new Date().toISOString().split('T')[0]}`, 'Requests', {
      title: 'Requests List Export',
      subtitle: `${exportData.length} request(s)`,
    });
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="requests-list-page">
      {error && <div className="error">{error}</div>}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0' }}>
        <button className="btn btn-outline btn-sm" onClick={() => setShowInfo(!showInfo)} title="Help">
          <HelpCircle size={18} />
        </button>
      </div>

      {showInfo && (
        <div className="info-banner">
          <div className="info-banner-header">
            <strong>{t('requests.title')}</strong>
            <button className="info-banner-close" onClick={() => setShowInfo(false)}><X size={16} /></button>
          </div>
          <p>This page lists all <strong>medical assistance requests</strong> from participating nations. Each request contains patients who need treatment and/or transport.</p>
          <ul>
            <li><strong>KPI Cards</strong> — Click a card to quickly filter by view (All, Active, My Requests, My Offers, In Transit).</li>
            <li><strong>Filters</strong> — Use Status, Category, and Urgency filters to narrow results. Combine with text search for precise lookup.</li>
            <li><strong>Save as Favorite</strong> — Save your current filter combination. Click "Saved View" to restore it later.</li>
            <li><strong>Export Excel</strong> — Download the filtered request list as an Excel spreadsheet.</li>
            <li><strong>Summary Column</strong> — Shows comment count, offer count, and patient count at a glance.</li>
          </ul>
        </div>
      )}

      {/* KPI View Cards */}
      <div className="views-kpi-grid">
        <div
          className={`views-kpi-card card ${activeView === 'all' ? 'active' : ''}`}
          onClick={() => handleViewChange('all')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{viewCounts.all}</div>
            <div className="kpi-label">{t('common.all')}</div>
          </div>
        </div>
        <div
          className={`views-kpi-card card ${activeView === 'active' ? 'active' : ''}`}
          onClick={() => handleViewChange('active')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{viewCounts.active}</div>
            <div className="kpi-label">{t('common.active')}</div>
          </div>
        </div>
        <div
          className={`views-kpi-card card ${activeView === 'myActive' ? 'active' : ''}`}
          onClick={() => handleViewChange('myActive')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{viewCounts.myActive}</div>
            <div className="kpi-label">My Active</div>
          </div>
        </div>
        <div
          className={`views-kpi-card card ${activeView === 'myRequests' ? 'active' : ''}`}
          onClick={() => handleViewChange('myRequests')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{viewCounts.myRequests}</div>
            <div className="kpi-label">My Requests</div>
          </div>
        </div>
        <div
          className={`views-kpi-card card ${activeView === 'myOffers' ? 'active' : ''}`}
          onClick={() => handleViewChange('myOffers')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{viewCounts.myOffers}</div>
            <div className="kpi-label">My Offers</div>
          </div>
        </div>
        <div
          className={`views-kpi-card card ${activeView === 'inTransit' ? 'active' : ''}`}
          onClick={() => handleViewChange('inTransit')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{viewCounts.inTransit}</div>
            <div className="kpi-label">{t('status.inTransit')}</div>
          </div>
        </div>
        <div
          className={`views-kpi-card card saved-view ${activeView === 'saved' ? 'active' : ''} ${!hasSavedView ? 'disabled' : ''}`}
          onClick={() => hasSavedView && handleViewChange('saved')}
        >
          <div className="kpi-content">
            <div className="kpi-value">{hasSavedView ? '...' : '--'}</div>
            <div className="kpi-label">Saved View</div>
          </div>
        </div>
        {!isReadOnlyInstance && ['NATION', 'EU', 'ADMIN'].includes(user?.role || '') && (
          <div
            className="views-kpi-card card create-request"
            onClick={() => navigate('/requests/new')}
          >
            <div className="kpi-content">
              <div className="kpi-value"><Plus size={20} /></div>
              <div className="kpi-label">{t('requests.createRequest')}</div>
            </div>
          </div>
        )}
      </div>

      {/* Filters Card */}
      <div className="list-filters card">
        <div className="filters-header">
          <h3>{t('common.filter')} & {t('common.search')}</h3>
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label>{t('common.search')}</label>
            <input
              type="text"
              placeholder={t('requests.searchRequests')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>{t('common.status')}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">{t('requests.filterByStatus')}</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{getStatusLabel(status)}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('common.category')}</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">{t('requests.filterByCategory')}</option>
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('requests.urgency')}</label>
            <select value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {availableUrgencies.map(urg => (
                <option key={urg} value={urg}>{urg}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-stats">
          <span>{t('common.showing')} {filteredRequests.length} {t('common.of')} {viewFilteredRequests.length} {t('requests.title').toLowerCase()}</span>
          <div className="filter-stats-actions">
            <button onClick={handleExportToExcel} className="btn btn-export" title="Export to Excel">
              <FileSpreadsheet size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {t('common.export')} Excel
            </button>
            <button onClick={handleSaveView} className="btn btn-sm btn-outline" title="Save current filter settings as favorite view">
              <Save size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Save as Favorite
            </button>
            <button onClick={clearFilters} className="btn btn-sm btn-outline">
              <X size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {t('common.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="requests-list card">
        {/* Pagination */}
        {filteredRequests.length > 0 && (
          <div className="pagination-bar">
            <div className="pagination-info">
              {t('common.showing')} {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredRequests.length)} {t('common.of')} {filteredRequests.length}
            </div>
            <div className="pagination-controls">
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                &laquo;
              </button>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                &lsaquo;
              </button>
              <span className="pagination-page">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === totalPages}
              >
                &rsaquo;
              </button>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                &raquo;
              </button>
            </div>
            <div className="pagination-size">
              <label>{t('common.perPage')}:</label>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}

        {paginatedRequests.length === 0 ? (
          <div className="empty-state">
            {requests.length === 0 ? (
              <>
                <Inbox size={48} strokeWidth={1.5} />
                <h3>{t('requests.noRequests')}</h3>
                <p>{t('requests.createFirstRequest')}</p>
                {!isReadOnlyInstance && ['NATION', 'EU', 'ADMIN'].includes(user?.role || '') && (
                  <button className="btn btn-primary" onClick={() => navigate('/requests/new')}>
                    <Plus size={16} /> {t('requests.createRequest')}
                  </button>
                )}
              </>
            ) : (
              <>
                <FileSearch size={48} strokeWidth={1.5} />
                <h3>{t('requests.noRequestsFound')}</h3>
                <p>{t('common.noResults')}</p>
                <button className="btn btn-outline" onClick={clearFilters}>
                  {t('common.clear')}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="requests-table">
              <thead>
                <tr>
                  <th onClick={() => handleColumnSort('requestId')} className="sortable">
                    {t('requests.requestId')} {getSortIndicator('requestId')}
                  </th>
                  <th>{t('requests.requestTitle')}</th>
                  <th onClick={() => handleColumnSort('from')} className="sortable">
                    {t('requests.fromNation')} {getSortIndicator('from')}
                  </th>
                  <th>UCPM Transit Hub</th>
                  <th onClick={() => handleColumnSort('category')} className="sortable">
                    {t('requests.category')} {getSortIndicator('category')}
                  </th>
                  <th>{t('common.summary')}</th>
                  <th>{t('requests.urgency')}</th>
                  <th onClick={() => handleColumnSort('status')} className="sortable">
                    {t('requests.status')} {getSortIndicator('status')}
                  </th>
                  <th onClick={() => handleColumnSort('date')} className="sortable">
                    {t('requests.created')} {getSortIndicator('date')}
                  </th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRequests.map((request) => (
                  <tr key={request.id} className="clickable-row">
                    <td className="request-id">
                      <Link to={`/requests/${request.id}`}>
                        {request.requestId}
                      </Link>
                    </td>
                    <td className="request-title">
                      <div className="title-cell">
                        <Link to={`/requests/${request.id}`}>
                          {request.title}
                        </Link>
                        {request.description && (
                          <div className="description-preview">
                            {request.description.substring(0, 80)}
                            {request.description.length > 80 && '...'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="nation-cell">
                      {(() => {
                        const nation = resolveNation(request.creator.nation);
                        return nation ? (
                          <div className="nation-flag-code" title={nation.name}>
                            <img src={nation.flagImage} alt={nation.name} className="nation-flag" />
                            <span className="nation-code">{nation.code}</span>
                          </div>
                        ) : request.creator.nation;
                      })()}
                    </td>
                    <td className="transit-hub-cell">
                      {(() => {
                        if (!request.exitPoint) return <span className="text-muted">—</span>;
                        const hubNation = request.exitPoint.nation ? resolveNation(request.exitPoint.nation) : null;
                        return (
                          <div className="transit-hub-trigger" style={{ position: 'relative' }}>
                            <button
                              className="transit-hub-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setHubPopupRequestId(hubPopupRequestId === request.id ? null : request.id);
                              }}
                              title={request.exitPoint.name}
                            >
                              {hubNation && <img src={hubNation.flagImage} alt={hubNation.name} className="nation-flag-sm" />}
                              <span className="nation-code">{hubNation?.code || request.exitPoint.nation || '?'}</span>
                            </button>
                            {hubPopupRequestId === request.id && (
                              <div className="hub-popup">
                                <div className="hub-popup-content">
                                  {hubNation && <img src={hubNation.flagImage} alt={hubNation.name} className="nation-flag" />}
                                  <div>
                                    <div className="hub-popup-name">{request.exitPoint.name}</div>
                                    <div className="hub-popup-nation">{hubNation?.name || request.exitPoint.nation}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <span className={`category-badge ${getCategoryBadgeClass(request.category)}`}>
                        {getCategoryLabel(request.category)}
                      </span>
                    </td>
                    <td className="stats-cell">
                      <div className="inline-stats">
                        {request.comments && request.comments.length > 0 && (
                          <span className="stat-item" title={t('common.comments')}>
                            <MessageSquare size={13} /> {request.comments.length}
                          </span>
                        )}
                        {request.offers && request.offers.length > 0 && (
                          <span className="stat-item" title={t('requests.offers')}>
                            <HandCoins size={13} /> {request.offers.length}
                          </span>
                        )}
                        {request.patients && request.patients.length > 0 && (
                          <span className="stat-item" title={t('requests.patients')}>
                            <Users size={13} /> {request.patients.length}
                          </span>
                        )}
                        {(() => {
                          const triage = getTriageSummary(request.patients);
                          if (!triage) return null;
                          return (
                            <span className="triage-badges" title="Patient Priority: P1/P2/P3">
                              {triage.p1 > 0 && <span className="triage-badge triage-p1">P1:{triage.p1}</span>}
                              {triage.p2 > 0 && <span className="triage-badge triage-p2">P2:{triage.p2}</span>}
                              {triage.p3 > 0 && <span className="triage-badge triage-p3">P3:{triage.p3}</span>}
                            </span>
                          );
                        })()}
                        {(() => {
                          const deadline = getDeadlineInfo(request.answerBy);
                          if (!deadline) return null;
                          return (
                            <span className={`deadline-badge ${deadline.className}`} title={`Answer by: ${new Date(request.answerBy!).toUTCString()}`}>
                              {deadline.text}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${request.urgency.toLowerCase()}`}>
                        {request.urgency}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                    </td>
                    <td className="date-cell">
                      <UTCTime date={request.createdAt} format="dd MMM yyyy HH:mm" />
                    </td>
                    <td className="actions-cell">
                      <button
                        onClick={(e) => handleQuickView(request.id, e)}
                        className="btn-quick-view"
                        title={t('common.viewDetails')}
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Request Quick View Modal */}
      <RequestQuickView
        requestId={quickViewRequestId}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
      />
    </div>
  );
}

export default RequestsList;
